/**
 * Monthly estimates vs actual (pure helpers, no data fetching).
 *   Estimate = what was planned for a month (tender_monthly_estimates).
 *   Actual   = paid bills, spread over the months of their bill period in proportion to the days
 *              of each month the period covers. A paid bill without a period counts in the month of its date.
 */
import type { FinancialTransaction, MonthlyEstimate } from '@/types/domain';

const DAY = 86_400_000;
const utc = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
const pad = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM' of a date. */
export const monthOf = (iso: string) => iso.slice(0, 7);
/** First day of a 'YYYY-MM' month as an ISO date. */
export const monthStart = (key: string) => `${key}-01`;
export function nextMonth(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${pad(m + 1)}`;
}
/** Months from start to end inclusive ('YYYY-MM'). Max 240 months. */
export function monthsBetween(startIso: string, endIso: string): string[] {
  const out: string[] = [];
  let k = monthOf(startIso);
  const last = monthOf(endIso);
  while (k <= last && out.length < 240) {
    out.push(k);
    k = nextMonth(k);
  }
  return out;
}
/** Financial year (April–March) containing the date: e.g. 2026-10-22 → { start: '2026-04', months: [...12] , label: 'FY 2026-27' }. */
export function financialYear(iso: string) {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const fy = m >= 4 ? y : y - 1;
  const months = monthsBetween(`${fy}-04-01`, `${fy + 1}-03-01`);
  return { start: months[0], end: months[11], months, label: `FY ${fy}-${String((fy + 1) % 100).padStart(2, '0')}` };
}

type PaidBill = Pick<FinancialTransaction, 'tenderId' | 'txnType' | 'status' | 'amount' | 'txnDate' | 'billPeriodFrom' | 'billPeriodTo'>;

/** Amount of one paid bill per month ('YYYY-MM' → ₹). */
export function allocateBill(b: PaidBill): Map<string, number> {
  const out = new Map<string, number>();
  if (!b.billPeriodFrom || !b.billPeriodTo || b.billPeriodTo < b.billPeriodFrom) {
    out.set(monthOf(b.txnDate), b.amount);
    return out;
  }
  const from = utc(b.billPeriodFrom);
  const to = utc(b.billPeriodTo);
  const total = (to - from) / DAY + 1;
  for (const k of monthsBetween(b.billPeriodFrom, b.billPeriodTo)) {
    const ms = Math.max(from, utc(`${k}-01`));
    const me = Math.min(to, utc(`${nextMonth(k)}-01`) - DAY);
    const days = (me - ms) / DAY + 1;
    if (days > 0) out.set(k, (out.get(k) ?? 0) + (b.amount * days) / total);
  }
  return out;
}

const isPaidBill = (b: Pick<FinancialTransaction, 'txnType' | 'status'>) => (b.txnType === 'invoice' || b.txnType === 'payment') && b.status === 'paid';

/** Actual per month (optionally per tender) from paid bills. */
export function actualByMonth(bills: PaidBill[], tenderId?: string): Map<string, number> {
  const out = new Map<string, number>();
  bills.forEach((b) => {
    if (!isPaidBill(b) || (tenderId && b.tenderId !== tenderId)) return;
    allocateBill(b).forEach((v, k) => out.set(k, (out.get(k) ?? 0) + v));
  });
  return out;
}

export interface EstimateRow {
  month: string;
  estimate: number | null;
  actual: number;
  /** actual − estimate (positive = spent more than planned); null when no estimate. */
  variance: number | null;
  cumEstimate: number;
  cumActual: number;
  cumVariance: number;
}

/** Month-by-month estimate vs actual with running (cumulative) totals. */
export function compareByMonth(months: string[], estimate: Map<string, number>, actual: Map<string, number>): EstimateRow[] {
  let cumE = 0;
  let cumA = 0;
  return months.map((month) => {
    const e = estimate.has(month) ? estimate.get(month)! : null;
    const a = actual.get(month) ?? 0;
    cumE += e ?? 0;
    cumA += a;
    return { month, estimate: e, actual: a, variance: e == null ? null : a - e, cumEstimate: cumE, cumActual: cumA, cumVariance: cumA - cumE };
  });
}

export const estimateMap = (rows: Pick<MonthlyEstimate, 'month' | 'estimatedAmount'>[]) =>
  new Map(rows.map((r) => [monthOf(r.month), r.estimatedAmount]));

/** Split a total evenly over months (₹, 2 decimals); the last month takes the rounding difference. */
export function spreadEvenly(total: number, months: string[]): Map<string, number> {
  const out = new Map<string, number>();
  if (!months.length || total <= 0) return out;
  const each = Math.floor((total / months.length) * 100) / 100;
  months.forEach((m, i) => out.set(m, i === months.length - 1 ? Math.round((total - each * (months.length - 1)) * 100) / 100 : each));
  return out;
}

export interface CsvEstimate {
  month: string;
  amount: number | null;
  quantity: number | null;
  remarks: string | null;
}

/** Split one CSV line, honouring double quotes. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; } else if (c === '"') q = false; else cur += c;
    } else if (c === '"') q = true;
    else if (c === ',') { out.push(cur); cur = ''; } else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
/** Accepts 2026-10, 2026-10-01, 10/2026, Oct 2026, Oct-26. Returns 'YYYY-MM' or null. */
export function parseMonth(s: string): string | null {
  const t = s.trim().toLowerCase();
  let m = /^(\d{4})-(\d{1,2})(?:-\d{1,2})?$/.exec(t);
  if (m) return Number(m[2]) >= 1 && Number(m[2]) <= 12 ? `${m[1]}-${pad(Number(m[2]))}` : null;
  m = /^(\d{1,2})[/-](\d{4})$/.exec(t);
  if (m) return Number(m[1]) >= 1 && Number(m[1]) <= 12 ? `${m[2]}-${pad(Number(m[1]))}` : null;
  m = /^([a-z]{3})[a-z]*[\s-]+(\d{2}|\d{4})$/.exec(t);
  if (m && MONTHS.includes(m[1])) return `${m[2].length === 2 ? `20${m[2]}` : m[2]}-${pad(MONTHS.indexOf(m[1]) + 1)}`;
  return null;
}

const num = (s: string | undefined) => {
  const v = (s ?? '').replace(/[₹,\s]/g, '');
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Parse an estimates CSV: columns Month, Estimated amount, Estimated quantity (optional), Remarks (optional).
 * The first line is a header when its first cell is not a month. Returns rows and readable errors.
 */
export function parseEstimatesCsv(text: string): { rows: CsvEstimate[]; errors: string[] } {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter((l) => l.trim());
  const rows: CsvEstimate[] = [];
  const errors: string[] = [];
  lines.forEach((line, i) => {
    const [m, a, q, ...rest] = splitCsvLine(line);
    const month = parseMonth(m ?? '');
    if (!month) {
      if (i > 0) errors.push(`Line ${i + 1}: "${m}" is not a month (use 2026-10 or Oct 2026).`);
      return;
    }
    const amount = num(a);
    const quantity = num(q);
    if (Number.isNaN(amount) || (amount != null && amount < 0)) return void errors.push(`Line ${i + 1}: amount "${a}" is not a valid number.`);
    if (Number.isNaN(quantity) || (quantity != null && quantity < 0)) return void errors.push(`Line ${i + 1}: quantity "${q}" is not a valid number.`);
    rows.push({ month, amount, quantity, remarks: rest.join(',').trim() || null });
  });
  return { rows, errors };
}
